import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  TrendingUp,
  Sun,
  Smile,
  Info,
  MessageSquare,
  Check,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { StageNav, type StageKey } from "@/components/marketing/StageNav";

const TOKENS = {
  ivory: "#FBF7F4",
  sand: "#FFE3E8",
  charcoal: "#1A0A24",
  white: "#FFFFFF",
  muted: "#3F2F4A",
  accent: "#B91C5C",
  accentSoft: "#FBE4EE",
};

const SERIF = "var(--font-display)";
const SERIF_ED = "var(--font-serif)";

export const Route = createFileRoute("/postmenopause")({
  head: () => ({
    meta: [
      { title: "Postmenopause — KnowMeno" },
      {
        name: "description",
        content:
          "Postmenopause: the years after your final period — longevity, bone and heart health, and what to focus on now.",
      },
      { property: "og:title", content: "Postmenopause — KnowMeno" },
      {
        property: "og:description",
        content:
          "Stage 3 of 3. The decades after menopause — vitality, longevity, and prevention.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://knowmeno.com/postmenopause" },
    ],
    links: [
      { rel: "canonical", href: "https://knowmeno.com/postmenopause" },
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        headline: "Postmenopause — KnowMeno",
        description: "Postmenopause: the years after your final period — longevity, bone and heart health, and what to focus on now.",
        url: "https://knowmeno.com/postmenopause",
        author: {
          "@type": "Organization",
          name: "KnowMeno",
        },
      }),
    }],
  }),
  component: PostmenopausePage,
});

function CountUp({ value, suffix = "" }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.textContent = `${value}${suffix}`;
      return;
    }
    let start: number | null = null;
    const dur = 1100;
    let raf = 0;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = `${Math.round(eased * value)}${suffix}`;
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, suffix]);
  return <span ref={ref}>0{suffix}</span>;
}




function PostmenopausePage() {
  const tiles = [
    { k: "1/3 of life", l: "Most women spend a third of their lives postmenopause" },
    { k: "Low + steady", l: "Estrogen settles at a low baseline" },
    { k: "Bone & heart", l: "Two systems that need extra attention now" },
    { k: "Symptoms ease", l: "Hot flashes typically calm over the years that follow" },
  ];

  const symptoms = [
    "Vaginal dryness",
    "Urinary changes",
    "Lower libido",
    "Sleep changes",
    "Joint aches",
    "Skin & hair changes",
    "Weight redistribution",
    "Mood shifts",
    "Some lingering hot flashes",
  ];

  const checkIns = [
    "Any vaginal bleeding after menopause — always worth checking",
    "Bone density (DEXA) screening — typically around age 65, earlier if at risk",
    "Heart health: blood pressure, cholesterol, and blood sugar checks",
    "Symptoms affecting your quality of life — options exist at every stage",
  ];

  return (
    <div style={{ background: TOKENS.ivory, color: TOKENS.charcoal }}>
      <SiteHeader />

      <div className="mx-auto max-w-6xl px-5 pt-6 sm:px-8">
        <p
          className="text-[12px] uppercase tracking-[0.22em]"
          style={{ color: TOKENS.muted }}
        >
          Understanding menopause · Stage 3 of 3
        </p>
        <div className="mt-6">
          <StageNav current={"post" satisfies StageKey} />
        </div>
      </div>

      {/* Hero */}
      <section className="px-5 pt-12 pb-10 sm:px-8 sm:pt-16 sm:pb-14">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: TOKENS.accent }}
            >
              Stage 03 of 3 · the decades that follow
            </p>
            <h1
              className="mt-4"
              style={{
                fontFamily: SERIF,
                fontSize: "clamp(44px, 8vw, 86px)",
                lineHeight: 1.02,
                letterSpacing: "-0.025em",
                color: TOKENS.charcoal,
              }}
            >
              Postmenopause
            </h1>
            <p
              className="mt-3"
              style={{
                fontFamily: SERIF_ED,
                fontStyle: "italic",
                fontSize: "clamp(22px, 3.4vw, 34px)",
                color: TOKENS.accent,
              }}
            >
              Vitality and longevity, after menopause.
            </p>
            <p
              className="mt-6 max-w-2xl text-[17px] leading-relaxed"
              style={{ color: TOKENS.muted }}
            >
              Postmenopause begins the day after you reach menopause and lasts for
              the rest of your life. Estrogen settles at a low baseline, and many
              of the symptoms that peaked around menopause gradually ease. This is
              the stage where longevity matters most — protecting bone, heart, and
              brain health for the decades ahead.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Quick facts */}
      <section className="px-5 py-16 sm:px-8 sm:py-20" style={{ background: TOKENS.sand + "55" }}>
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <p
              className="mb-8 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: TOKENS.accent }}
            >
              <TrendingUp className="h-4 w-4" aria-hidden /> The quick facts
            </p>
          </ScrollReveal>

          <div className="grid gap-6 lg:grid-cols-2">
            <ScrollReveal>
              <div className="flex flex-col gap-5">
                <div
                  className="km-lift flex items-end gap-5 rounded-3xl p-7"
                  style={{
                    background: TOKENS.accentSoft,
                    border: `1px solid ${TOKENS.accent}33`,
                  }}
                >
                  <div>
                    <div
                      className="text-[13px] font-semibold"
                      style={{ color: TOKENS.accent, opacity: 0.85 }}
                    >
                      years on average
                    </div>
                    <div
                      style={{
                        fontFamily: SERIF,
                        fontSize: "clamp(56px, 11vw, 88px)",
                        lineHeight: 0.88,
                        letterSpacing: "-0.03em",
                        color: TOKENS.accent,
                      }}
                    >
                      <CountUp value={30} suffix="+" />
                    </div>
                  </div>
                  <p
                    className="mb-2 max-w-[210px] text-[14px] leading-snug"
                    style={{ color: TOKENS.muted }}
                  >
                    With today's life expectancy, most women live 30+ years
                    postmenopause — roughly a third of life.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {tiles.map((t) => (
                    <div
                      key={t.k}
                      className="km-lift rounded-2xl p-4"
                      style={{
                        background: TOKENS.white,
                        border: `1px solid ${TOKENS.sand}aa`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: SERIF,
                          fontSize: 18,
                          color: TOKENS.accent,
                          lineHeight: 1.15,
                        }}
                      >
                        {t.k}
                      </div>
                      <div
                        className="mt-1 text-[13px] leading-snug"
                        style={{ color: TOKENS.muted }}
                      >
                        {t.l}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delayMs={120}>
              <div
                className="km-lift h-full rounded-3xl p-7"
                style={{
                  background: TOKENS.white,
                  border: `1px solid ${TOKENS.sand}aa`,
                }}
              >
                <p
                  className="mb-4 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em]"
                  style={{ color: TOKENS.accent }}
                >
                  <Sun className="h-4 w-4" aria-hidden /> What's happening in your body
                </p>
                <p className="text-[15px] leading-relaxed" style={{ color: TOKENS.muted }}>
                  Estrogen stays at a{" "}
                  <strong style={{ color: TOKENS.charcoal }}>low, steady baseline</strong>.
                  Bone density and cardiovascular risk profiles shift — which is why
                  screenings and prevention become the focus.
                </p>
                <div
                  className="mt-6 border-t pt-4"
                  style={{ borderColor: `${TOKENS.sand}aa`, borderTopStyle: "dashed" }}
                >
                  <div
                    className="mb-2 flex justify-between text-[11.5px] font-semibold"
                    style={{ color: TOKENS.muted, letterSpacing: "0.04em" }}
                  >
                    <span>Estrogen over time</span>
                    <span>low, steady baseline</span>
                  </div>
                  <svg viewBox="0 0 240 80" className="block h-auto w-full">
                    <line
                      x1="0"
                      y1="40"
                      x2="240"
                      y2="40"
                      stroke={`${TOKENS.sand}`}
                      strokeWidth="1"
                      strokeDasharray="3 4"
                    />
                    <path
                      d="M0,32 C16,20 30,22 46,34 C62,46 78,56 96,64 C114,70 130,72 150,72 C170,72 192,72 240,72"
                      fill="none"
                      stroke={TOKENS.accent}
                      strokeWidth="2.6"
                      strokeLinecap="round"
                    />
                    <circle cx="200" cy="72" r="4.5" fill={TOKENS.white} stroke={TOKENS.accent} strokeWidth="2.4" />
                  </svg>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="px-5 py-16 sm:px-8 sm:py-20" style={{ background: TOKENS.ivory }}>
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <p
              className="mb-5 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: TOKENS.accent }}
            >
              <Smile className="h-4 w-4" aria-hidden /> What you might notice
            </p>
            <p
              className="mb-7 max-w-2xl text-[16px] leading-relaxed"
              style={{ color: TOKENS.muted }}
            >
              Many menopause symptoms{" "}
              <strong style={{ color: TOKENS.charcoal }}>ease over time</strong>,
              while a few — especially genitourinary changes — can persist or first
              appear in postmenopause and are very treatable.
            </p>
            <div className="flex flex-wrap gap-2.5">
              {symptoms.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13.5px]"
                  style={{
                    background: TOKENS.accentSoft,
                    color: TOKENS.charcoal,
                    border: `1px solid ${TOKENS.accent}28`,
                  }}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: TOKENS.accent }}
                    aria-hidden
                  />
                  {s}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Good to know */}
      <section className="px-5 pb-4 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <div
              className="km-lift flex gap-4 rounded-2xl p-5"
              style={{
                background: TOKENS.white,
                border: `1px solid ${TOKENS.sand}aa`,
                borderLeft: `4px solid ${TOKENS.accent}`,
              }}
            >
              <div
                className="grid h-10 w-10 flex-none place-items-center rounded-xl"
                style={{ background: TOKENS.accentSoft, color: TOKENS.accent }}
                aria-hidden
              >
                <Info className="h-5 w-5" />
              </div>
              <div>
                <p
                  className="mb-1 text-[12px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: TOKENS.accent }}
                >
                  Good to know
                </p>
                <p className="text-[15px] leading-relaxed" style={{ color: TOKENS.muted }}>
                  Any bleeding after menopause is not normal — even a single spot.
                  Don't wait it out; check in with your clinician so it can be
                  evaluated promptly.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* When to check in */}
      <section className="px-5 py-16 sm:px-8 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal>
            <div
              className="km-lift rounded-3xl p-7"
              style={{
                background: TOKENS.white,
                border: `1px solid ${TOKENS.sand}aa`,
              }}
            >
              <p
                className="mb-5 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em]"
                style={{ color: TOKENS.accent }}
              >
                <MessageSquare className="h-4 w-4" aria-hidden /> When to check in with
                your clinician
              </p>
              <ul className="grid gap-3">
                {checkIns.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-[15px]" style={{ color: TOKENS.muted }}>
                    <Check
                      className="mt-0.5 h-5 w-5 flex-none"
                      style={{ color: TOKENS.accent }}
                      aria-hidden
                    />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <p
                className="mt-5 text-[12.5px]"
                style={{ color: TOKENS.muted, opacity: 0.85 }}
              >
                General guidance only — your clinician can tell you what's right for your
                situation.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Previous */}
      <section className="px-5 pb-16 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:justify-start">
          <Link
            to="/menopause"
            className="km-lift flex flex-col rounded-2xl border px-5 py-3 transition"
            style={{
              background: TOKENS.white,
              borderColor: `${TOKENS.sand}aa`,
              minWidth: 220,
            }}
          >
            <span
              className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em]"
              style={{ color: TOKENS.muted }}
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Previous stage
            </span>
            <span
              style={{
                fontFamily: SERIF,
                fontSize: 18,
                color: TOKENS.charcoal,
              }}
            >
              Menopause
            </span>
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <footer className="px-5 pb-16 sm:px-8">
        <div className="mx-auto max-w-6xl">
          <div
            className="flex items-start gap-4 border-t pt-6"
            style={{ borderColor: `${TOKENS.sand}aa` }}
          >
            <ShieldCheck
              className="h-7 w-7 flex-none"
              style={{ color: TOKENS.accent }}
              aria-hidden
            />
            <div>
              <p className="text-[13.5px] leading-relaxed" style={{ color: TOKENS.muted }}>
                <strong style={{ color: TOKENS.charcoal }}>
                  These are educational insights to help you understand patterns — not
                  medical advice, diagnosis, or treatment.
                </strong>{" "}
                Every woman's experience is different. Talk to your clinician about
                what's right for you.
              </p>
              <p
                className="mt-2 text-[12px]"
                style={{ color: TOKENS.muted, opacity: 0.75 }}
              >
                Figures reflect general population data from NIH MedlinePlus, Cleveland
                Clinic, StatPearls, and the Study of Women's Health Across the Nation
                (SWAN).
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
